import React, { useEffect, useState } from 'react';
import firebase from '../firebaseConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserPlus,
    faClock,
    faHashtag,
    faKey,
    faUserCheck,
    faCopy,
    faCircleCheck,
    faCircleArrowLeft,
    faWandMagicSparkles,
    faEye,
    faEyeSlash,
} from '@fortawesome/free-solid-svg-icons';
import Toggle from './Toggle';

const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_TTL_MS = 30 * DAY_MS;
const MAX_USES_CAP = 50;

const toLocalInput = (ms) => {
    const d = new Date(ms);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
        `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Mirrors iOS ArtistInviteSheetVC. Mints a tokenized /a/{token} invite that
// lets the redeemer join the artist as a member. Stacked sub-view of the
// artist picker.
const ArtistInviteComposer = ({ artist, onBack, onClose }) => {
    const [useExpiry, setUseExpiry] = useState(true);
    const [expiresAt, setExpiresAt] = useState(toLocalInput(Date.now() + 7 * DAY_MS));
    const [useMaxUses, setUseMaxUses] = useState(true);
    const [maxUses, setMaxUses] = useState(1);
    const [usePassword, setUsePassword] = useState(false);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [requiresApproval, setRequiresApproval] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onBack(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onBack]);

    const create = async () => {
        if (busy) return;
        setError('');
        const data = { artistId: artist.id };

        if (useExpiry && expiresAt) {
            const ms = new Date(expiresAt).getTime();
            if (!Number.isFinite(ms) || ms <= Date.now()) {
                setError('Pick an expiry date in the future.');
                return;
            }
            if (ms > Date.now() + MAX_TTL_MS) {
                setError('Expiry can be at most 30 days out.');
                return;
            }
            data.expiresAt = ms;
        }
        if (useMaxUses) {
            const n = Number(maxUses);
            if (!Number.isInteger(n) || n < 1) {
                setError('Max uses must be a whole number of at least 1.');
                return;
            }
            if (n > MAX_USES_CAP) {
                setError(`Max uses can be at most ${MAX_USES_CAP}.`);
                return;
            }
            data.maxUses = n;
        }
        if (usePassword && password) data.password = password;
        if (requiresApproval) data.requiresApproval = true;

        setBusy(true);
        try {
            const fn = firebase.functions().httpsCallable('createArtistInvite');
            const res = await fn(data);
            setResult({ token: res?.data?.token, url: res?.data?.url || null });
        } catch (err) {
            setError(err?.message || "Couldn't create the invite.");
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        if (!result?.url) return;
        try {
            await navigator.clipboard.writeText(result.url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch (_e) { /* clipboard blocked */ }
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-card share-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-eyebrow eyebrow-share">
                    <FontAwesomeIcon icon={faUserPlus} />
                    <span>INVITE MEMBER</span>
                </div>
                <div className="modal-title">{artist.name}</div>

                {!result ? (
                    <>
                        <div className="share-row">
                            <span className="share-row-label">
                                <span className={`share-row-icon ${useExpiry ? 'is-warn' : ''}`}>
                                    <FontAwesomeIcon icon={faClock} />
                                </span>
                                Expires
                            </span>
                            <Toggle
                                checked={useExpiry}
                                disabled={busy}
                                onChange={(e) => setUseExpiry(e.target.checked)}
                            />
                        </div>
                        {useExpiry && (
                            <div className="share-row indent">
                                <span className="share-row-label">When</span>
                                <input
                                    className="share-text-input"
                                    type="datetime-local"
                                    value={expiresAt}
                                    disabled={busy}
                                    onChange={(e) => setExpiresAt(e.target.value)}
                                />
                            </div>
                        )}

                        <div className="share-row">
                            <span className="share-row-label">
                                <span className={`share-row-icon ${useMaxUses ? 'is-info' : ''}`}>
                                    <FontAwesomeIcon icon={faHashtag} />
                                </span>
                                Limit uses
                            </span>
                            <Toggle
                                checked={useMaxUses}
                                accent="blue"
                                disabled={busy}
                                onChange={(e) => setUseMaxUses(e.target.checked)}
                            />
                        </div>
                        {useMaxUses && (
                            <div className="share-row indent">
                                <span className="share-row-label">Max uses</span>
                                <input
                                    className="share-text-input"
                                    type="number"
                                    min="1"
                                    max={MAX_USES_CAP}
                                    value={maxUses}
                                    disabled={busy}
                                    onChange={(e) => setMaxUses(e.target.value)}
                                />
                            </div>
                        )}

                        <div className="share-row">
                            <span className="share-row-label">
                                <span className={`share-row-icon ${usePassword ? 'is-locked' : ''}`}>
                                    <FontAwesomeIcon icon={faKey} />
                                </span>
                                Require password
                            </span>
                            <Toggle
                                checked={usePassword}
                                accent="purple"
                                disabled={busy}
                                onChange={(e) => setUsePassword(e.target.checked)}
                            />
                        </div>
                        {usePassword && (
                            <div className="share-row indent">
                                <span className="share-row-label">Password</span>
                                <div className="share-password-input">
                                    <input
                                        className="share-text-input"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        disabled={busy}
                                        placeholder="••••••"
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="share-password-eye"
                                        onClick={() => setShowPassword((v) => !v)}
                                        disabled={busy}
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="share-row">
                            <span className="share-row-label">
                                <span className={`share-row-icon ${requiresApproval ? 'is-info' : ''}`}>
                                    <FontAwesomeIcon icon={faUserCheck} />
                                </span>
                                Require approval
                            </span>
                            <Toggle
                                checked={requiresApproval}
                                accent="blue"
                                disabled={busy}
                                onChange={(e) => setRequiresApproval(e.target.checked)}
                            />
                        </div>

                        {error && <div className="new-project-error">{error}</div>}

                        <div className="new-project-actions">
                            <button className="btn-ghost" onClick={onBack} disabled={busy}>
                                <FontAwesomeIcon icon={faCircleArrowLeft} /> Back
                            </button>
                            <button className="btn-primary btn-glow" onClick={create} disabled={busy}>
                                <FontAwesomeIcon icon={faWandMagicSparkles} />
                                <span>{busy ? 'Generating…' : 'Generate invite'}</span>
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="composer-success">
                            <div className="composer-success-icon">
                                <FontAwesomeIcon icon={faCircleCheck} />
                            </div>
                            <div className="composer-success-title">Invite ready</div>
                            <div className="composer-success-sub">
                                Anyone with this link can join {artist.name}
                                {requiresApproval ? ' once you approve their request' : ''}
                                {usePassword ? ' (with the password)' : ''}.
                            </div>
                            <div className="share-link-preview">
                                <FontAwesomeIcon icon={faUserPlus} className="share-link-preview-icon" />
                                <code>{result.url}</code>
                                <button className={`btn-ghost ${copied ? 'is-copied' : ''}`} onClick={copy}>
                                    <FontAwesomeIcon icon={copied ? faCircleCheck : faCopy} />
                                    {copied ? ' Copied' : ' Copy'}
                                </button>
                            </div>
                        </div>
                        <div className="new-project-actions">
                            <button className="btn-ghost" onClick={onBack}>Back</button>
                            <button className="btn-primary" onClick={onClose}>Done</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ArtistInviteComposer;
